import { ref, reactive, computed, onActivated, onMounted, nextTick, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { request } from '@/util/globalContext'
import { listCommon } from '@/util/vueutil/commonList.js'
import { baseURL } from '@/util/request'
import axios from 'axios'

// 检车状态映射
const checkStatusMap = {
    '0': '已预约',
    '1': '已到站',
    '2': '外检中',
    '3': '环检中',
    '4': '安检中',
    '5': '待取证',
    '6': '已取证',
    '7': '已年检'
}

// 预约类型映射
const appointmentTypeMap = {
    '0': '微信检车预约',
    '1': '现场检车预约',
    '2': '微信保险预约',
    '3': '现场保险预约'
}

// 预约状态映射
const appointmentStatusMap = {
    '0': '有效',
    '1': '已取消'
}

// 检车状态下拉选项
const checkStatusOptions = [
    { value: '0', label: '已预约' },
    { value: '1', label: '已到站' },
    { value: '2', label: '外检中' },
    { value: '3', label: '环检中' },
    { value: '4', label: '安检中' },
    { value: '5', label: '待取证' },
    { value: '6', label: '已取证' }
]

// 预约筛选下拉选项
const appointmentFilterOptions = [
    { value: 'booked', label: '已预约' },
    { value: 'unbooked', label: '未预约' }
]

// 检车状态流程步骤（用于表格末列进度条展示）
const checkFlowSteps = ['已预约', '已到站', '外检中', '环检中', '安检中', '待取证', '已取证', '已年检']

// 判断检车状态按钮是否显示（全部显示，不再隐藏）
const shouldShowCheckButton = () => {
    return true
}

// 判断检车状态按钮是否禁用
const isCheckButtonDisabled = (row, idx) => {
    const currentStatus = Number(row.checkStatus)
    const targetStatus = Number(idx)
    
    // 已年检(7)：全部禁用（终态）
    if (currentStatus === 7) {
        return true
    }
    
    // 已取证(6)：只能点7
    if (currentStatus === 6) {
        return targetStatus !== 7
    }
    
    // 待取证(5)：只能点6
    if (currentStatus === 5) {
        return targetStatus !== 6
    }
    
    // 已预约~安检中(0-4)：只能在0-5之间变更
    if (currentStatus < 5) {
        if (targetStatus > 5) {
            return true  // 不能跳已取证/已年检
        }
        // 流程状态非"正常"时禁用
        if (row.processStatus !== '0') {
            return true
        }
    }
    
    return false
}

export default function useBillList() {
    const listRef = ref(null)
    const selectedRows = ref([])

    // 流程状态下拉选项
    const processStatusOptions = [
        { value: '0', label: '正常' },
        { value: '1', label: '暂停' },
        { value: '2', label: '处理中' }
    ]

    // 搜索表单
    const searchForm = reactive({
        appointmentFilter: 'booked',
        plateNumber: '',
        userName: '',
        vin: '',
        checkStatus: '',
        processStatus: ''
    })

    // 引入通用方法
    const { handleReset } = listCommon(searchForm)

    // 加载列表
    const handleLoadList = async (paginationParams) => {
        if (!listRef.value) return
        if (!listRef.value.isInited) {
            await nextTick()
            if (!listRef.value.isInited) {
                ElMessage.warning('列表组件初始化中，请稍后重试')
                return
            }
        }

        listRef.value.setLoading(true)
        try {
            const params = {
                ...paginationParams,
                appointmentFilter: searchForm.appointmentFilter,
                plateNumber: searchForm.plateNumber,
                userName: searchForm.userName,
                vin: searchForm.vin,
                checkStatus: searchForm.checkStatus,
                processStatus: searchForm.processStatus
            }
            const res = await request.get('/checkHandle/getList', { params })
            if (res.code === 200) {
                listRef.value.updateListData(res.data.records || [])
                listRef.value.updatePagination({
                    pageNum: paginationParams.pageNum,
                    pageSize: paginationParams.pageSize,
                    total: res.data.total || 0
                })
            } else {
                ElMessage.error('列表加载失败：' + (res.msg || '接口异常'))
                listRef.value.updateListData([])
                listRef.value.updatePagination({ ...paginationParams, total: 0 })
            }
        } catch (error) {
            ElMessage.error('列表加载失败：网络异常')
            listRef.value.updateListData([])
            listRef.value.updatePagination({ ...paginationParams, total: 0 })
        } finally {
            listRef.value.setLoading(false)
        }
    }

    onMounted(async () => {
        await nextTick()
        setTimeout(() => {
            if (listRef.value?.isInited) {
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            }
        }, 50)
    })

    onActivated(() => {
        if (listRef.value?.isInited) {
            const pagination = listRef.value.getPagination()
            handleLoadList(pagination)
        }
    })

    // ========== 现场检车预约补充信息 dialog ==========
    const supplementDialog = reactive({
        visible: false,
        plateNumber: '',
        submitting: false,
        // 缓存用于提交时重试的原始参数
        retryParams: null
    })
    const supplementForm = reactive({
        contactPhone: '',
        agentPhone: ''
    })
    const supplementFormRef = ref(null)

    // 打开补充信息对话框
    const openSupplementDialog = (row, retryParams) => {
        supplementDialog.plateNumber = row.plateNumber || '-'
        supplementDialog.retryParams = retryParams
        supplementForm.contactPhone = ''
        supplementForm.agentPhone = ''
        supplementDialog.visible = true
        // 下一次事件循环后重置校验（防止复用上次的错误提示）
        nextTick(() => {
            if (supplementFormRef.value) supplementFormRef.value.clearValidate()
        })
    }

    // 提交补充信息
    const submitSupplement = async () => {
        const contactPhone = String(supplementForm.contactPhone || '').trim()
        const agentPhone = String(supplementForm.agentPhone || '').trim()
        const phoneReg = /^1[3-9]\d{9}$/

        // 手动校验（不依赖 form ref，避免 ref 绑定问题导致无响应）
        if (!contactPhone) {
            ElMessage.warning('联系电话不能为空')
            return
        }
        if (!phoneReg.test(contactPhone)) {
            ElMessage.warning('联系电话格式不正确')
            return
        }
        if (agentPhone && !phoneReg.test(agentPhone)) {
            ElMessage.warning('带检人电话格式不正确')
            return
        }

        // 触发 form UI 校验提示（可选，ref 为 null 时跳过）
        if (supplementFormRef.value) {
            try { await supplementFormRef.value.validate() } catch { return }
        }

        supplementDialog.submitting = true
        try {
            const retry = supplementDialog.retryParams || {}
            const res = await request.post('/checkHandle/updateField', {
                ...retry,
                contactPhone,
                agentPhone: agentPhone || undefined
            })
            if (res.code === 200) {
                supplementDialog.visible = false
                ElMessage.success('状态变更成功')
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            } else {
                ElMessage.error('提交失败：' + (res.msg || '接口异常'))
            }
        } catch {
            ElMessage.error('提交失败：网络异常')
        } finally {
            supplementDialog.submitting = false
        }
    }

    // 通用字段变更（检车状态/流程状态）
    const handleFieldChange = async (row, field, value, valueLabel, title) => {
        if (row[field] === value) return

        const message = `确认将${title}变更为「${valueLabel}」吗？\n车牌号：${row.plateNumber}`
        let remark = undefined

        if (field === 'processStatus') {
            const isPause = value === '1'
            const isProcessing = value === '2'
            if (isProcessing) {
                // 处理中：弹确认框（防止误触），不需要备注
                try {
                    await ElMessageBox.confirm(
                        `${row.plateNumber} 变更为处理中吗？`,
                        '流程状态变更',
                        {
                            confirmButtonText: '确认变更',
                            cancelButtonText: '取消',
                            type: 'warning'
                        }
                    )
                } catch {
                    return
                }
            } else {
                // 暂停/正常：弹备注输入框
                try {
                    const result = await ElMessageBox.prompt(
                        `${row.plateNumber} 变更为${valueLabel}吗`,
                        '流程状态变更',
                        {
                            confirmButtonText: '确认变更',
                            cancelButtonText: '取消',
                            inputType: 'textarea',
                            inputValue: isPause ? '检车异常，请到8号窗口处理' : '',
                            inputPlaceholder: isPause ? '请输入暂停原因（必填，最多300字）' : '选填备注（最多300字，不填则清空原有备注）',
                            inputValidator: (val) => {
                                if (isPause && (!val || !val.trim())) return '暂停原因不能为空'
                                if (val && val.length > 300) return '备注不能超过300字'
                                return true
                            }
                        }
                    )
                    remark = result.value || ''
                } catch {
                    return
                }
            }
        } else {
            // 未预约列表 + 已到站：已确定无预约，直接弹补充信息对话框（省一次接口调用）
            if (field === 'checkStatus' && value === '1' && searchForm.appointmentFilter === 'unbooked') {
                const params = { id: row.id, field, value }
                openSupplementDialog(row, params)
                return
            }
            // 已到站(1) 已预约列表：跳过确认框，直接调接口（后端判断有预约直接变更，无预约返回1001兜底）
            if (!(field === 'checkStatus' && value === '1')) {
                try {
                    await ElMessageBox.confirm(message, '状态变更确认', {
                        confirmButtonText: '确认变更',
                        cancelButtonText: '取消',
                        type: 'warning'
                    })
                } catch {
                    return
                }
            }
        }

        try {
            const params = { id: row.id, field, value }
            if (remark !== undefined) params.remark = remark
            const res = await request.post('/checkHandle/updateField', params)
            if (res.code === 200) {
                ElMessage.success('状态变更成功')
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            } else {
                ElMessage.error('状态变更失败：' + (res.msg || '接口异常'))
            }
        } catch {
            ElMessage.error('状态变更失败：网络异常')
        }
    }

    // 多选选中项变更
    const handleSelectionChange = (val) => {
        selectedRows.value = val || []
    }

    // 更新年检周期（勾选检车记录时只更新选中的；未勾选时更新全部已绑定车辆）
    const handleUpdateCheckCycle = async () => {
        const selectedIds = selectedRows.value.map(row => row.id)
        const hasSelected = selectedIds.length > 0
        const confirmMsg = hasSelected
            ? `确认更新选中的 ${selectedIds.length} 条检车记录对应车辆的年检周期？更新完会生成Excel记录。`
            : '未选择检车记录，将更新所有已绑定车辆的年检周期，更新完会生成Excel记录。确认继续？'

        try {
            await ElMessageBox.confirm(confirmMsg, '更新年检周期', {
                confirmButtonText: '确认更新',
                cancelButtonText: '取消',
                type: 'warning',
                center: true
            })

            // 文件流下载，不能用通用request（会被响应拦截器处理为json）
            const token = sessionStorage.getItem('token')
            const currentRoleStr = sessionStorage.getItem('currentRole')
            const res = await axios.post(baseURL + '/wxapi/updateCheckCycle',
                { checkInfoIds: hasSelected ? selectedIds : [] },
                {
                    responseType: 'blob',
                    headers: {
                        Authorization: token ? `Bearer ${token}` : '',
                        'X-RoleId': currentRoleStr || ''
                    }
                }
            )

            // 兼容后端校验失败或抛错时返回json
            const contentType = res.headers['content-type'] || ''
            if (contentType.includes('application/json')) {
                const text = await res.data.text()
                const json = JSON.parse(text)
                ElMessage.error(json.msg || '更新失败')
                return
            }

            // 构造下载链接
            const disposition = res.headers['content-disposition'] || ''
            let fileName = '年检周期变更记录.xlsx'
            const match = disposition.match(/filename\*?=(?:UTF-8'')?([^;]+)/i)
            if (match && match[1]) {
                try { fileName = decodeURIComponent(match[1].replace(/["']/g, '')) } catch (e) { /* ignore */ }
            }
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.download = fileName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(link.href)

            ElMessage.success('年检周期更新完成，Excel已开始下载，请对照确认是否需要再手动调整')
            const pagination = listRef.value.getPagination()
            handleLoadList(pagination)
        } catch (error) {
            if (error === 'cancel') return
            ElMessage.error('操作失败：' + (error.message || '网络异常'))
        }
    }

    // ========== 更新检车信息对话框 ==========
    const updateDialogVisible = ref(false)
    const cycleLoading = ref(false)
    const allLoading = ref(false)
    const updateForm = reactive({
        vehicleId: null,
        plateNumber: '',
        vin: '',
        vehicleType: '',
        useNature: '',
        registerDate: '',
        vehicleStatus: '',
        checkCycle: 12,
        checkExpireDate: '',
        checkType: ''
    })

    // 车辆状态文本
    const vehicleStatusText = computed(() => {
        const map = { '1': '审核中', '2': '已绑定', '3': '审核失败' }
        return map[updateForm.vehicleStatus] || '-'
    })

    // 入参 YYYY-MM-DD（type=month 默认给出当月第一天），返回当月最后一天
    const getLastDayOfMonth = (dateStr) => {
        if (!dateStr) return ''
        const [y, m] = dateStr.split('-').map(Number)
        const lastDay = new Date(y, m, 0).getDate()
        return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
    }

    // 选完月份后（type=month 默认给的是当月第一天），自动替换成当月最后一天
    watch(
        () => updateForm.checkExpireDate,
        (newVal) => {
            if (!newVal) return
            const lastDay = getLastDayOfMonth(newVal)
            // 避免死循环：只有不是最后一天时才替换
            if (newVal !== lastDay) {
                updateForm.checkExpireDate = lastDay
            }
        }
    )

    // 日历控件：只能选当年的月份
    const disabledDate = (time) => {
        const currentYear = new Date().getFullYear()
        return time.getFullYear() !== currentYear
    }

    // 点击"更新"按钮，打开对话框
    const handleUpdateCheckInfo = (row) => {
        updateForm.vehicleId = row.vehicleId
        updateForm.plateNumber = row.plateNumber || ''
        updateForm.vin = row.vin || ''
        updateForm.vehicleType = row.vehicleType || ''
        updateForm.useNature = row.useNature || ''
        updateForm.registerDate = row.registerDate ? String(row.registerDate).substring(0, 10) : ''
        updateForm.vehicleStatus = row.vehicleStatus || ''
        updateForm.checkCycle = row.checkCycle || 12
        updateForm.checkExpireDate = row.checkExpireDate ? String(row.checkExpireDate).substring(0, 10) : ''
        updateForm.checkType = row.checkType || ''
        updateDialogVisible.value = true
    }

    // 更新周期（单独按钮，无确认框）
    const saveCheckCycle = async () => {
        if (!updateForm.vehicleId) {
            ElMessage.warning('车辆信息异常')
            return
        }
        if (!updateForm.checkCycle || updateForm.checkCycle < 1) {
            ElMessage.warning('检验周期必须为正整数')
            return
        }
        cycleLoading.value = true
        try {
            const res = await request.post('/wxapi/updateCheckCycleById', {
                vehicleId: updateForm.vehicleId,
                checkCycle: updateForm.checkCycle
            })
            if (res.code === 200) {
                ElMessage.success('检验周期更新成功')
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            } else {
                ElMessage.error('更新失败：' + (res.msg || '接口异常'))
            }
        } catch {
            ElMessage.error('更新失败：网络异常')
        } finally {
            cycleLoading.value = false
        }
    }

    // 全部更新（周期+到期日+类型，有确认框）
    const saveAllCheckInfo = async () => {
        if (!updateForm.vehicleId) {
            ElMessage.warning('车辆信息异常')
            return
        }
        if (!updateForm.checkCycle || updateForm.checkCycle < 1) {
            ElMessage.warning('检验周期必须为正整数')
            return
        }
        if (!updateForm.checkExpireDate) {
            ElMessage.warning('请选择检验到期日')
            return
        }
        if (!updateForm.checkType) {
            ElMessage.warning('请选择检车类型')
            return
        }
        try {
            await ElMessageBox.confirm('确认更新检车信息？', '更新确认', {
                confirmButtonText: '确认更新',
                cancelButtonText: '取消',
                type: 'warning',
                center: true
            })
        } catch {
            return
        }
        allLoading.value = true
        try {
            const res = await request.post('/wxapi/updateCheckInfoByHandle', {
                vehicleId: updateForm.vehicleId,
                checkCycle: updateForm.checkCycle,
                checkExpireDate: updateForm.checkExpireDate,
                checkType: updateForm.checkType
            })
            if (res.code === 200) {
                ElMessage.success('检车信息更新成功')
                updateDialogVisible.value = false
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            } else {
                ElMessage.error('更新失败：' + (res.msg || '接口异常'))
            }
        } catch {
            ElMessage.error('更新失败：网络异常')
        } finally {
            allLoading.value = false
        }
    }

    return {
        searchForm,
        listRef,
        selectedRows,
        handleLoadList,
        handleReset,
        handleSelectionChange,
        handleUpdateCheckCycle,
        handleUpdateCheckInfo,
        updateDialogVisible,
        updateForm,
        vehicleStatusText,
        disabledDate,
        saveCheckCycle,
        saveAllCheckInfo,
        cycleLoading,
        allLoading,
        checkStatusMap,
        appointmentTypeMap,
        appointmentStatusMap,
        checkStatusOptions,
        appointmentFilterOptions,
        processStatusOptions,
        checkFlowSteps,
        shouldShowCheckButton,
        isCheckButtonDisabled,
        handleFieldChange,
        supplementDialog,
        supplementForm,
        supplementFormRef,
        submitSupplement
    }
}
